import React, { useState } from 'react';
import { useGameStore } from '../store/gameStore';
import RewardPopup from './RewardPopup';
import { 
  FlayerTask, SubgramTask, TraffyTask, CustomTask, TgrassOffer, TaddyFeedItem,
  tasksAPI, subgramAPI, traffyAPI, customTasksAPI, tgrassAPI, taddyAPI 
} from '../services/api';

// Определяем универсальный тип задания, включающий все возможные виды
interface UniversalTaskProps {
  taskType: 'flayer' | 'subgram' | 'traffy' | 'custom' | 'tgrass' | 'taddy';
  task: FlayerTask | SubgramTask | TraffyTask | CustomTask | TgrassOffer | TaddyFeedItem;
  onComplete: () => void;
}

type RewardType = 'stars' | 'tickets';

// Функция для перевода стандартных действий на русский язык
const translateAction = (taskText: string): string => {
  // Проверяем содержит ли текст задания стандартные английские фразы
  if (taskText.toLowerCase().includes('start bot')) {
    return taskText.replace(/start bot/i, 'Запустить бота');
  } else if (taskText.toLowerCase().includes('subscribe channel')) {
    return taskText.replace(/subscribe channel/i, 'Подписаться на канал');
  } else if (taskText.toLowerCase().includes('give boost')) {
    return taskText.replace(/give boost/i, 'Дать буст');
  } else if (taskText.toLowerCase().includes('follow link')) {
    return taskText.replace(/follow link/i, 'Перейти по ссылке');
  } else if (taskText.toLowerCase().includes('perform action')) {
    return taskText.replace(/perform action/i, 'Выполнить действие');
  }
  return taskText;
};

const UniversalTaskCard: React.FC<UniversalTaskProps> = ({ taskType, task, onComplete }) => {
  const [checking, setChecking] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [showReward, setShowReward] = useState(false);
  const [earnedReward, setEarnedReward] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasExecuted, setHasExecuted] = useState(false);
  const [earnedRewardType, setEarnedRewardType] = useState<RewardType>('stars');
  const { addStars, addTickets, setTickets } = useGameStore();

  // Сбрасываем состояние при изменении задания
  React.useEffect(() => {
    setIsCompleted(false);
    setHasExecuted(false);
    setError(null);
    setRetryCount(0);
  }, [taskType, task]);

  // Получение необходимых свойств задания в зависимости от его типа
  const getTaskProperties = () => {
    if (taskType === 'flayer') {
      const flayerTask = task as FlayerTask;
      const primaryLink = (flayerTask.link && flayerTask.link.trim().length > 0)
        ? flayerTask.link
        : (flayerTask.links && flayerTask.links.length > 0 ? flayerTask.links[0] : '');
      return {
        name: flayerTask.name || 'Задание',
        description: flayerTask.task || '',
        link: primaryLink,
        id: flayerTask.signature,
        reward: 5,
        rewardType: 'tickets' as RewardType,
      };
    } else if (taskType === 'subgram') {
      const subgramTask = task as SubgramTask;
      return {
        name: subgramTask.resource_name || 'Подписка на канал',
        description: 'Подписаться и получить 5 билетиков',
        link: subgramTask.link,
        id: subgramTask.link,
        reward: 5,
        rewardType: 'tickets' as RewardType,
      };
    } else if (taskType === 'traffy') {
      const traffyTask = task as TraffyTask;
      return {
        name: traffyTask.title || 'Задание',
        description: 'Перейти по ссылке и выполнить условия',
        link: traffyTask.link,
        id: traffyTask.id,
        reward: 10,
        rewardType: 'stars' as RewardType,
      };
    } else if (taskType === 'custom') {
      const customTask = task as CustomTask;
      return {
        name: customTask.channelName || customTask.botName || customTask.resourceName || customTask.title,
        description: customTask.requiredAction || customTask.description || 'Выполните задание и получите награду',
        link: customTask.channelLink || customTask.botLink || customTask.resourceLink || '',
        id: customTask.id.toString(),
        reward: customTask.reward,
        rewardType: 'stars' as RewardType,
      };
    } else if (taskType === 'tgrass') {
      const tgrassTask = task as TgrassOffer;
      return {
        name: tgrassTask.name || 'Подписка на канал',
        description: 'Подпишитесь на канал и получите 5 билетиков',
        link: tgrassTask.link,
        id: tgrassTask.link,
        reward: 5,
        rewardType: 'tickets' as RewardType,
      };
    } else if (taskType === 'taddy') {
      const taddyTask = task as TaddyFeedItem;
      return {
        name: taddyTask.title || 'Задание',
        description: taddyTask.description || 'Выполните задание и получите 5 билетиков',
        link: taddyTask.link,
        id: (taddyTask as any).id || (taddyTask as any).uid,
        reward: 5,
        rewardType: 'tickets' as RewardType,
      };
    }
    
    return {
      name: 'Задание',
      description: 'Выполните задание для получения награды',
      link: '',
      id: '',
      rewardType: 'stars' as RewardType,
    };
  };

  const { name, description, link, id, reward, rewardType = 'stars' } = getTaskProperties();
  const displayReward = reward ?? (rewardType === 'tickets' ? 5 : 50);
  const rewardIcon = rewardType === 'tickets' ? '🎟️' : '⭐';

  // Нормализация ссылок перед открытием, чтобы корректно работали и кастомные задания
  const normalizeLink = (rawLink: string): string => {
    if (!rawLink) return '';
    let url = rawLink.trim();

    // Если в ссылке есть лишний текст перед реальным URL (например: "Link https://t.me/...")
    // находим первое вхождение протокола/домена и обрезаем всё до него
    const protoIndex = (() => {
      const candidates = [
        url.indexOf('https://'),
        url.indexOf('http://'),
        url.indexOf('tg://'),
        url.indexOf('t.me/'),
      ].filter((i) => i >= 0);
      if (candidates.length === 0) return -1;
      return Math.min(...candidates);
    })();

    if (protoIndex > 0) {
      url = url.slice(protoIndex).trim();
    }

    // Если ссылка вида t.me/xxx без протокола — добавляем https://
    if (!url.startsWith('http://') && !url.startsWith('https://') && url.startsWith('t.me/')) {
      url = `https://${url}`;
    }

    // Если ссылка Telegram в виде tg://resolve?domain=xxx — конвертируем в https://t.me/xxx
    if (url.startsWith('tg://resolve?')) {
      try {
        const parsed = new URL(url);
        const domain = parsed.searchParams.get('domain');
        if (domain) {
          url = `https://t.me/${domain}`;
        }
      } catch {
        // если не распарсилось, оставляем как есть
      }
    }

    // Если вообще нет протокола и это похоже на домен — добавляем https://
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('tg://')) {
      if (url.includes('.')) {
        url = `https://${url}`;
      }
    }

    return url;
  };

  const handleExecuteTask = async () => {
    if (taskType === 'taddy') {
      setError(null);
      const Taddy: any = (window as any).Taddy;
      const exchange = Taddy && typeof Taddy.exchange === 'function' ? Taddy.exchange() : null;
      if (!exchange) {
        if (window.Telegram && window.Telegram.WebApp && link.includes('t.me/')) {
          window.Telegram.WebApp.openTelegramLink(link);
        } else {
          window.open(link, '_blank');
        }
        return;
      }
      const item = task as TaddyFeedItem;
      await exchange.open(item).catch(() => {
        setError('Задание еще не выполнено. Убедитесь, что вы выполнили все условия.');
      });
      return;
    }

    const openUrl = normalizeLink(link);
    if (!openUrl) {
      return;
    }

    if (window.Telegram && window.Telegram.WebApp) {
      // Внутри Telegram Mini App всегда открываем через WebApp API
      if (openUrl.includes('t.me/')) {
        window.Telegram.WebApp.openTelegramLink(openUrl);
      } else if (typeof (window.Telegram.WebApp as any).openLink === 'function') {
        (window.Telegram.WebApp as any).openLink(openUrl);
      } else {
        window.open(openUrl, '_blank');
      }
    } else {
      // Вне Telegram открываем в новом окне/вкладке
      window.open(openUrl, '_blank');
    }

    if (taskType === 'custom') {
      const customTask = task as CustomTask;
      if (customTask.type === 'resource') {
        setHasExecuted(true);
      }
    }
  };

  const handleCheckTask = async () => {
    setChecking(true);
    setError(null);
    
    try {
      let isTaskCompleted = false;
      let reward = displayReward; // Значение по умолчанию
      let resolvedRewardType: RewardType = rewardType;
      let ticketsSynced = false;
      
      // Проверка и выполнение задания в зависимости от типа
      if (taskType === 'flayer') {
        const checkResult = await tasksAPI.checkFlayerTask(id);
        if (!checkResult.success) throw new Error(checkResult.error || 'Ошибка проверки');
        isTaskCompleted = checkResult.isCompleted;
        
        if (isTaskCompleted) {
          const completeResult = await tasksAPI.completeFlayerTask(id);
          if (completeResult.success) {
            reward = completeResult.reward;
            if (completeResult.newTickets !== undefined) {
              setTickets(completeResult.newTickets);
              ticketsSynced = true;
            }
          } else {
            throw new Error(completeResult.error || 'Ошибка получения награды');
          }
        }
      } 
      else if (taskType === 'subgram') {
        const checkResult = await subgramAPI.checkSubgramTask(id);
        if (!checkResult.success) throw new Error(checkResult.error || 'Ошибка проверки');
        isTaskCompleted = checkResult.isCompleted;
        
        if (isTaskCompleted) {
          const completeResult = await subgramAPI.completeSubgramTask(id);
          if (completeResult.success) {
            reward = completeResult.reward || displayReward;
            if (completeResult.newTickets !== undefined) {
              setTickets(completeResult.newTickets);
              ticketsSynced = true;
            }
          } else {
            throw new Error(completeResult.error || 'Ошибка получения награды');
          }
        }
      } 
      else if (taskType === 'traffy') {
        const checkResult = await traffyAPI.checkTraffyTask(id);
        if (!checkResult.success) throw new Error(checkResult.error || 'Ошибка проверки');
        isTaskCompleted = checkResult.isCompleted;
        
        if (isTaskCompleted) {
          const completeResult = await traffyAPI.completeTraffyTask(id, checkResult.token || '');
          if (completeResult.success) {
            reward = completeResult.reward || 10;
            resolvedRewardType = 'stars';
          } else {
            throw new Error(completeResult.error || 'Ошибка получения награды');
          }
        }
      }
      else if (taskType === 'custom') {
        const customTask = task as CustomTask;
        
        // Для заданий типа "resource" (ресурс без проверки) проверяем, было ли выполнено действие
        if (customTask.type === 'resource') {
          if (!hasExecuted) {
            setError('Задание еще не выполнено. Убедитесь, что вы выполнили все условия.');
            return;
          }
        }
        
        const completeResult = await customTasksAPI.completeCustomTask(parseInt(id));
        if (completeResult.success) {
          isTaskCompleted = true;
          reward = completeResult.reward;
          resolvedRewardType = 'stars';
        } else {
          throw new Error(completeResult.message || 'Ошибка выполнения задания');
        }
      }
      else if (taskType === 'tgrass') {
        const checkResult = await tgrassAPI.checkTgrassOffer(id);
        if (!checkResult.success) throw new Error(checkResult.error || 'Ошибка проверки');
        isTaskCompleted = checkResult.isCompleted;
        
        if (isTaskCompleted) {
          const completeResult = await tgrassAPI.completeTgrassOffer(id);
          if (completeResult.success) {
            reward = completeResult.reward || displayReward;
            if (completeResult.newTickets !== undefined) {
              setTickets(completeResult.newTickets);
              ticketsSynced = true;
            }
          } else {
            throw new Error(completeResult.error || 'Ошибка получения награды');
          }
        }
      }
      else if (taskType === 'taddy') {
        const Taddy: any = (window as any).Taddy;
        const exchange = Taddy && typeof Taddy.exchange === 'function' ? Taddy.exchange() : null;
        if (exchange) {
          const item = task as TaddyFeedItem;
          const success = await exchange.check(item);
          isTaskCompleted = !!success;
        } else {
          const checkResult = await taddyAPI.checkTask(id);
          if (!checkResult.success) throw new Error('Ошибка проверки');
          isTaskCompleted = checkResult.isCompleted;
        }

        if (isTaskCompleted) {
          const completeResult = await taddyAPI.completeTask(id);
          if (completeResult.success) {
            reward = completeResult.reward || displayReward;
            if (completeResult.newTickets !== undefined) {
              setTickets(completeResult.newTickets);
              ticketsSynced = true;
            }
          } else {
            throw new Error('Ошибка получения награды');
          }
        }
      }
      
      if (isTaskCompleted) {
        // Добавляем награду
        if (resolvedRewardType === 'tickets') {
          if (!ticketsSynced) {
            addTickets(reward);
          }
        } else {
          addStars(reward);
        }
        setEarnedReward(reward);
        setEarnedRewardType(resolvedRewardType);
        setIsCompleted(true);
        setShowReward(true);
      } else {
        setError('Задание еще не выполнено. Убедитесь, что вы выполнили все условия.');
      }
    } catch (err: any) {
      console.error('Error checking task:', err);
      
      if (err.message?.includes('network') || 
          err.message?.includes('timeout') || 
          err.message?.includes('Network Error') ||
          err.code === 'ECONNABORTED') {
        setError('Ошибка соединения. Попробуйте повторить.');
      } else if (err.response?.status === 500) {
        setError('Ошибка сервера. Попробуйте повторить.');
      } else {
        setError('Ошибка при проверке задания. Попробуйте позже.');
      }
    } finally {
      setChecking(false);
      setCompleting(false);
    }
  };

  const handleCloseReward = () => {
    setShowReward(false);
    
    // Только после закрытия попапа с наградой обновляем список заданий
    if (isCompleted) {
      onComplete();
    }
  };

  // Переводим текст задания
  const translatedTaskText = translateAction(description);

  return (
    <>
      <div className={`task-card task-card-regular ${isCompleted ? 'task-card-completed' : ''}`}>
        <div className="task-card-header">
          <div className="task-card-title">{name}</div>
          <div className="task-card-reward reward-regular">{displayReward} <span className="reward-star">{rewardIcon}</span></div>
        </div>
        
        <div className="task-card-description">
          {translatedTaskText}
        </div>
        
        <div className="task-card-features">
          <div className="task-feature">✓ Моментальное начисление бонуса</div>
          <div className="task-feature">✓ Простое выполнение</div>
        </div>
        
        <div className="task-button-group">
          <button 
            className="task-card-button-gray" 
            onClick={handleExecuteTask}
            disabled={checking || completing || isCompleted}
          >
            Выполнить
          </button>
          
          <button 
            className="task-card-button" 
            onClick={handleCheckTask}
            disabled={checking || completing || isCompleted}
          >
            {checking ? 'Проверяем...' : 
             completing ? 'Начисляем...' : 
             isCompleted ? 'Выполнено' : 'Проверить'}
          </button>
        </div>
        
        {error && (
          <div className="task-error">
            {error}
            {(error.includes('соединения') || error.includes('сервера')) && (
              <button 
                className="task-retry-button" 
                onClick={() => {
                  setRetryCount(0);
                  handleCheckTask();
                }}
                disabled={checking || completing}
              >
                Повторить
              </button>
            )}
          </div>
        )}
        

      </div>
      
      {/* Всплывающее окно с наградой */}
      <RewardPopup 
        isOpen={showReward}
        reward={earnedReward}
        icon={earnedRewardType === 'tickets' ? '🎟️' : '⭐'}
        onClose={handleCloseReward}
      />
    </>
  );
};

export default UniversalTaskCard;